import { useState } from 'react';

const FLAGS = [
  {
    flag: 'great',    emoji: '😊', label: 'הרגיש טוב',
    bg: 'rgba(52,211,153,0.10)', border: 'rgba(52,211,153,0.30)', color: '#5dcaa5',
  },
  {
    flag: 'hard_day', emoji: '😮‍💨', label: 'היה קשה',
    bg: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.25)', color: '#c4b5fd',
  },
  {
    flag: 'hard_day', emoji: '🛏️', label: 'עייף', key: 'tired',
    bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.20)', color: '#a59fc8',
  },
  {
    flag: 'sick',     emoji: '🌡️', label: 'הייתי חולה',
    bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.20)', color: '#a59fc8',
  },
  {
    flag: 'hot',      emoji: '🔥', label: 'היה חם',
    bg: 'rgba(251,146,60,0.10)', border: 'rgba(251,146,60,0.30)', color: '#fb923c',
  },
];

const IDLE_BTN = {
  background: 'rgba(139,92,246,0.05)',
  border: '1px solid rgba(139,92,246,0.15)',
  color: '#6b6590',
};

const FLAG_BY_CODE = Object.fromEntries(
  FLAGS.map(f => [f.flag, f])
);

export default function ContextFlagSection({ workout, onSave, onSkip, saving, initialFlag, initialNote }) {
  const initSaved = (() => {
    if (!initialFlag || initialFlag === 'normal') return null;
    const f = FLAG_BY_CODE[initialFlag];
    return f ? { flag: initialFlag, emoji: f.emoji, label: f.label, color: f.color, note: initialNote || null } : null;
  })();

  const [selected, setSelected] = useState(null);
  const [note, setNote]         = useState('');
  const [showNote, setShowNote] = useState(false);
  const [savedFlag, setSavedFlag] = useState(initSaved);

  const toggle = (i) => setSelected(prev => prev === i ? null : i);

  const handleSave = async () => {
    const f = selected != null ? FLAGS[selected] : null;
    const flagCode = f ? f.flag : 'normal';
    const trimNote = note.trim() || null;

    // Optimistically show saved state before parent unmounts us
    setSavedFlag({
      flag: flagCode,
      emoji: f ? f.emoji : '✅',
      label: f ? f.label : 'רגיל',
      color: f ? f.color : '#8b85b0',
      note: trimNote,
    });

    onSave(flagCode, trimNote);
  };

  // ── Saved state ───────────────────────────────────────────────────────────
  if (savedFlag) {
    return (
      <div style={{ direction: 'rtl' }}>
        <div style={{ fontSize: 12, color: '#8b85b0', marginBottom: 6 }}>איך הרגשת?</div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(139,92,246,0.06)',
          border: '1px solid rgba(139,92,246,0.18)',
          borderRadius: 10, padding: '7px 12px',
        }}>
          <span style={{ fontSize: 17 }}>{savedFlag.emoji}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: savedFlag.color, fontWeight: 600 }}>
              {savedFlag.label}
            </div>
            {savedFlag.note && (
              <div style={{ fontSize: 11, color: '#8b85b0', marginTop: 2 }}>
                {savedFlag.note}
              </div>
            )}
          </div>
          <button
            onClick={() => setSavedFlag(null)}
            style={{
              all: 'unset', cursor: 'pointer',
              fontSize: 11, color: '#6b6590',
              padding: '2px 6px',
              border: '1px solid rgba(139,92,246,0.2)',
              borderRadius: 6,
            }}
          >שנה</button>
        </div>
      </div>
    );
  }

  // ── Input state ───────────────────────────────────────────────────────────
  return (
    <div style={{ direction: 'rtl' }}>
      <div style={{ fontSize: 13, color: '#d6d2ea', marginBottom: 10 }}>
        איך הרגשת?{' '}
        <span style={{ fontSize: 11, color: '#8b85b0' }}>(עוזר לנו לכוון את האימונים)</span>
      </div>

      {/* Emoji buttons — full row */}
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
        {FLAGS.map((f, i) => {
          const isSelected = selected === i;
          return (
            <button
              key={f.key || f.flag + i}
              onClick={() => toggle(i)}
              style={{
                all: 'unset',
                cursor: 'pointer',
                fontSize: 12,
                padding: '6px 12px',
                borderRadius: 16,
                border: `1px solid ${isSelected ? f.border : 'rgba(139,92,246,0.15)'}`,
                background: isSelected ? f.bg : 'rgba(139,92,246,0.04)',
                color: isSelected ? f.color : '#6b6590',
                display: 'inline-flex', alignItems: 'center', gap: 5,
                transition: 'all 0.15s',
              }}
            >
              <span style={{ fontSize: 15 }}>{f.emoji}</span>
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Optional note */}
      {!showNote ? (
        <button
          onClick={() => setShowNote(true)}
          style={{
            all: 'unset', cursor: 'pointer',
            fontSize: 12, color: '#6b6590',
            background: 'rgba(139,92,246,0.05)',
            border: '1px solid rgba(139,92,246,0.20)',
            borderRadius: 10, padding: '8px 12px',
            display: 'block', width: '100%', boxSizing: 'border-box',
            marginBottom: 10,
          }}
        >
          ✏️ רוצה להוסיף משהו? (לא חובה)
        </button>
      ) : (
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="רצית להוסיף משהו?"
          rows={2}
          style={{
            width: '100%', boxSizing: 'border-box',
            fontSize: 12, background: 'rgba(139,92,246,0.05)',
            border: '1px solid rgba(139,92,246,0.20)',
            borderRadius: 10, padding: '8px 12px',
            color: '#b8b2d8', resize: 'none', outline: 'none',
            marginBottom: 10, fontFamily: 'inherit',
          }}
        />
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            all: 'unset', cursor: saving ? 'default' : 'pointer',
            flex: 1, textAlign: 'center',
            background: 'linear-gradient(135deg,#7c3aed,#4f46e5)',
            color: '#fff', fontSize: 13, fontWeight: 500,
            padding: '10px', borderRadius: 10,
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'שומר...' : 'שמור'}
        </button>
        <button
          onClick={onSkip}
          disabled={saving}
          style={{
            all: 'unset', cursor: 'pointer',
            fontSize: 12, color: '#8b85b0',
            padding: '10px 16px',
            border: '1px solid rgba(139,92,246,0.20)',
            borderRadius: 10,
          }}
        >
          דלג (רגיל)
        </button>
      </div>
    </div>
  );
}
